import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import EmployeeFormModal from './EmployeeFormModal.jsx';

const TrashIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;

// Masked everywhere except the actual edit form — a SIN shouldn't be
// readable at a glance in a list view even inside the office.
function maskSin(sin) {
  if (!sin) return '—';
  const digits = sin.replace(/\D/g, '');
  if (digits.length < 4) return '•••';
  return `••• ••• ${digits.slice(-3)}`;
}

export default function EmployeeList() {
  const [employees, setEmployees] = useState(null);
  const [query, setQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [country, setCountry] = useState('Canada');

  useEffect(() => { api('/settings').then(s => setCountry(s.country || 'Canada')).catch(() => {}); }, []);

  function load() {
    setEmployees(null);
    api('/dispatch/employees').then(setEmployees).catch(() => setEmployees([]));
  }

  const deleteEmployee = async (e, emp) => {
    e.stopPropagation();
    const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(' ');
    if (!window.confirm(`Delete "${fullName}"? This can't be undone.`)) return;
    await api(`/dispatch/employees/${emp.id}`, { method: 'DELETE' });
    load();
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!employees) return [];
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      [e.first_name, e.last_name, e.ref_number, e.position, e.department, e.email]
        .filter(Boolean).some(v => v.toLowerCase().includes(q)));
  }, [employees, query]);

  return (
    <div>
      <PageHeader
        icon={Icon.control}
        title="Employees"
        subtitle="Office, yard, and payroll staff — including self-employed contractor drivers"
        color={SECTION_COLOR.control}
        actions={<button className="primary-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>+ New employee</button>}
      />

      <div className="panel filter-row-panel">
        <div className="filter-row">
          <input placeholder="Type to filter..." value={query} onChange={e => setQuery(e.target.value)} style={{ width: 240 }} />
        </div>
      </div>

      {employees === null && <div className="empty">Loading...</div>}
      {employees && employees.length === 0 && <div className="empty">No employees yet — click "+ New employee" above.</div>}

      {employees && employees.length > 0 && (
        <table>
          <thead>
            <tr><th>Ref#</th><th>Name</th><th>Position</th><th>Department</th><th>Type</th><th>{country === 'United States' ? 'SSN' : 'SIN'}</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} onClick={() => { setEditing(e); setFormOpen(true); }} style={{ cursor: 'pointer' }}>
                <td>{e.ref_number || '—'}</td>
                <td>{[e.first_name, e.last_name].filter(Boolean).join(' ')}</td>
                <td>{e.position || '—'}</td>
                <td>{e.department || '—'}</td>
                <td>{e.employee_type}</td>
                <td>{maskSin(e.sin)}</td>
                <td>{e.status || 'Active'}</td>
                <td>
                  <button type="button" title="Delete employee" onClick={ev => deleteEmployee(ev, e)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--red)', cursor: 'pointer', padding: 0 }}>
                    <TrashIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {formOpen && (
        <EmployeeFormModal employee={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
      )}
    </div>
  );
}
